import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useStore } from "../../../store";
import { parseKeywordSearch } from "../keywordParams";

interface Props {
  /** 드릴다운 화면에서 현재 키워드를 미리 채워 둘 때 쓴다(해시태그면 # 포함). */
  initialValue?: string;
  width?: number;
}

/**
 * 키워드 검색 입력. 첫 글자가 # 이면 해시태그 정확 일치, 아니면 설명 본문 단어
 * 검색이다 — 필터의 키워드 유형도 그에 맞게 바뀐다. 제출하면 현재 국가·기간으로
 * `/keywords/:keyword` 드릴다운을 연다. 본문 검색은 여러 단어를 띄어 쓰면 모두
 * 포함하는 템플릿을 찾는다. 서버로 보내기 전에 앞뒤 공백을 지운다.
 */
const KeywordSearch = ({ initialValue = "", width = 300 }: Props) => {
  const {
    KeywordsStore: { searchKeyword },
  } = useStore();
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const canSubmit = parseKeywordSearch(value) !== null;

  const submit = () => {
    const path = searchKeyword(value);
    if (!path) return;
    navigate(path);
  };

  return (
    <Tooltip
      title="Start with # to search a hashtag exactly (e.g. #diwali). Without #, every word must appear in the description text."
      arrow
      placement="top"
    >
      <TextField
        size="small"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.nativeEvent.isComposing) {
            event.preventDefault();
            submit();
          }
        }}
        InputProps={{
          inputProps: { "aria-label": "Search keyword", maxLength: 100 },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="search"
                onClick={submit}
                disabled={!canSubmit}
                edge="end"
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ "&.MuiFormControl-root.MuiTextField-root": { width } }}
      />
    </Tooltip>
  );
};

export default observer(KeywordSearch);
